export const NOTES_PREVIEW_LENGTH = 150;

/**
 * Strips HTML tags from a string, returning plain text content.
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Validates a bookmark name: must be 1-255 chars after trimming.
 * Returns { valid, trimmed, error }.
 */
export function validateBookmarkName(name: string): {
  valid: boolean;
  trimmed: string;
  error: string;
} {
  const trimmed = name.trim();
  if (trimmed.length < 1) {
    return { valid: false, trimmed, error: 'Bookmark name is required.' };
  }
  if (trimmed.length > 255) {
    return {
      valid: false,
      trimmed,
      error: 'Bookmark name must not exceed 255 characters.',
    };
  }
  return { valid: true, trimmed, error: '' };
}

/**
 * Computes notes preview data from raw HTML notes.
 */
export function computeNotesPreview(htmlNotes: string): {
  plainText: string;
  truncated: string;
  exceedsPreview: boolean;
} {
  const plainText = stripHtml(htmlNotes);
  const exceedsPreview = plainText.length > NOTES_PREVIEW_LENGTH;
  const truncated = exceedsPreview
    ? plainText.slice(0, NOTES_PREVIEW_LENGTH) + '...'
    : plainText;
  return { plainText, truncated, exceedsPreview };
}

/**
 * Normalizes a bookmark URL into a link-friendly absolute href.
 *
 * - Returns null for empty/whitespace-only URLs (caller should render plain text, not a link).
 * - Leaves URLs with an existing scheme (http:, https:, mailto:, ftp:, etc.) untouched.
 * - Prepends "https://" to schemeless URLs (e.g. "www.example.com" -> "https://www.example.com")
 *   so the browser treats them as absolute instead of relative to the current origin.
 */
export function normalizeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Already has a scheme (http://, https://, mailto:, ftp://, etc.)
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return trimmed;
  }

  // Protocol-relative URL (//example.com)
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }

  // Schemeless — default to https
  return `https://${trimmed}`;
}
